import { type Locale } from '@aysar/contracts';
import { useMemo } from 'react';

import { useDirectionContext } from '@/shared/theme/DirectionProvider/DirectionProvider';
import { type Direction } from '@/shared/theme/DirectionProvider/useDirectionProviderLogic';

/** A logical side, as a component should think about placement. */
export type LogicalSide = 'start' | 'end' | 'top' | 'bottom';
/** A physical side, which is what a positioning library ultimately needs. */
export type PhysicalSide = 'left' | 'right' | 'top' | 'bottom';

export interface DirectionApi {
  locale: Locale;
  direction: Direction;
  isRtl: boolean;
  /**
   * Resolves a logical side to a physical one.
   *
   * This is the ONE sanctioned place to branch on direction (U7). Styling never
   * does — it uses `ms-`/`me-`/`start-`/`end-`, and the lint law rejects the
   * physical spellings. But a library prop that only accepts `left | right`
   * (Radix `side`, a chart's legend position) has to be told, and the honest
   * answer is to convert here rather than to litter `isRtl ? 'left' : 'right'`
   * ternaries across components.
   *
   * The block axis passes through untouched: `top` and `bottom` do not mirror.
   */
  physicalSide: (side: LogicalSide) => PhysicalSide;
}

export function useDirection(): DirectionApi {
  const context = useDirectionContext();

  if (context === null) {
    throw new Error('useDirection must be used inside <DirectionProvider>.');
  }

  const { locale, direction } = context;

  return useMemo(() => {
    const isRtl = direction === 'rtl';

    return {
      locale,
      direction,
      isRtl,
      physicalSide: (side: LogicalSide): PhysicalSide => {
        if (side === 'top' || side === 'bottom') return side;
        if (side === 'start') return isRtl ? 'right' : 'left';
        return isRtl ? 'left' : 'right';
      },
    };
  }, [locale, direction]);
}
